import type { JSX } from 'react';
import { Check } from 'lucide-react';
import { Container } from '@components/ui/Container';
import { RevealOnView } from '@components/ui/RevealOnView';
import { landingCopy } from '@features/landing/data/landing.pt';

const STAGGER_DELAY_MS = 110;

/**
 * Secao "Diferenciais / Por que a Omanna" (SECT-06 — PRD §4.7).
 *
 * Visual distintivo da landing: faixa dark-green full-width que cria contraste
 * com as secoes light (Servicos `bg-white`, Como Funciona `bg-neutral-50`),
 * inspirado na peca de divulgacao do Lucas (.planning/design-reference.md —
 * "trust-badges row" bg-brand-700/800/900).
 *
 * Background: `bg-brand-900` (#073A2F — verde mais profundo do tema).
 * Layout:
 *  - Title centralizado em branco (font-display, text-3xl-to-4xl)
 *  - 4 items em 2x2 grid (mobile) / 4 cols inline (lg+)
 *  - Cada item: filled Check icon brand-300 + bold lead (white) + body
 *    (white/80 opacity = `text-brand-100/90`)
 *  - Reveal staggered de 110ms
 *
 * Decisao visual (W-?): em vez do checklist plano do PRD, adotamos a
 * apresentacao em banda dark-green do design reference do Lucas. Conteudo
 * (bold lead + body) preservado do PRD §4.7. Cria momento "feature" antes do
 * FAQ — quebra do ritmo cream/white da landing.
 *
 * Acessibilidade:
 *  - `<section id="diferenciais" aria-labelledby="diferenciais-heading">`
 *  - Cards usam `<article>` com Check icon `aria-hidden` (texto e o rotulo)
 *  - Contraste WCAG AA verificado: white em brand-900 = ~13:1 (AAA),
 *    brand-100 em brand-900 = ~8:1 (AAA)
 */
export const Diferenciais = (): JSX.Element => {
  const { diferenciais } = landingCopy;

  return (
    <section
      id="diferenciais"
      aria-labelledby="diferenciais-heading"
      className="relative overflow-hidden bg-brand-900 py-20 text-white lg:py-28"
    >
      <Container>
        <RevealOnView className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-300 mb-3">
            {diferenciais.eyebrow}
          </p>
          <h2
            id="diferenciais-heading"
            className="font-display text-4xl font-bold leading-tight md:text-5xl"
          >
            {diferenciais.title}
          </h2>
        </RevealOnView>

        <ul className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {diferenciais.items.map((item, index) => (
            <RevealOnView key={item.lead} delayMs={STAGGER_DELAY_MS * (index + 1)}>
              <li className="flex h-full flex-col items-start gap-4">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-500 text-white shadow-md ring-2 ring-brand-700/40">
                  <Check className="h-5 w-5" strokeWidth={3} aria-hidden="true" />
                </div>
                <div>
                  <p className="font-display text-lg font-bold leading-snug text-white">
                    {item.lead}
                  </p>
                  <p className="mt-2 text-base leading-relaxed text-brand-100/90">{item.body}</p>
                </div>
              </li>
            </RevealOnView>
          ))}
        </ul>
      </Container>
    </section>
  );
};
